import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Users, Plus, Copy, LogOut, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [newName, setNewName] = useState("");
  const [inviteCode, setInviteCode] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/teams");
      setTeams(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createTeam = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/teams", { name: newName.trim() });
      setTeams((p) => [data, ...p]);
      setNewName("");
      toast.success("Team created");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const joinTeam = async () => {
    if (!inviteCode.trim()) return;
    setJoining(true);
    try {
      const { data } = await api.post("/teams/join", { invite_code: inviteCode.trim() });
      setTeams((p) => [data, ...p.filter((t) => t.id !== data.id)]);
      setInviteCode("");
      toast.success(`Joined ${data.name}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setJoining(false);
    }
  };

  const leaveOrDelete = async (team) => {
    const msg = team.is_owner ? "Delete this team? All shared notes will be unlinked." : "Leave this team?";
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/teams/${team.id}`);
      setTeams((p) => p.filter((t) => t.id !== team.id));
      toast.success(team.is_owner ? "Team deleted" : "Left team");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyInvite = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Invite code copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F9FF]">
      <Navbar />

      <main className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">/ Teams</p>
        <h1 className="mt-2 font-heading font-black text-4xl sm:text-6xl tracking-tighter">Shared Spaces</h1>
        <p className="mt-3 text-zinc-600 max-w-xl">
          Create a team to share notes with classmates or colleagues. Anyone with the invite code can join.
        </p>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          <div className="bg-white border-2 border-black p-6">
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">/ Create team</p>
            <h2 className="mt-2 font-heading font-bold text-2xl">Start a new space</h2>
            <input
              data-testid="new-team-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Biology 101 Study Group"
              className="w-full mt-4 px-4 py-3 bg-[#F0F9FF] border-2 border-black focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2"
            />
            <button
              data-testid="create-team-btn"
              onClick={createTeam}
              disabled={creating || !newName.trim()}
              className="mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-5 py-3 bg-[#3B82F6] text-white btn-brutal disabled:opacity-50"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create
            </button>
          </div>

          <div className="bg-white border-2 border-black p-6">
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">/ Join team</p>
            <h2 className="mt-2 font-heading font-bold text-2xl">Have an invite code?</h2>
            <input
              data-testid="join-code"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              placeholder="Paste invite code"
              className="w-full mt-4 px-4 py-3 bg-[#F0F9FF] border-2 border-black font-mono focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2"
            />
            <button
              data-testid="join-team-btn"
              onClick={joinTeam}
              disabled={joining || !inviteCode.trim()}
              className="mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-5 py-3 border-2 border-black hover:bg-black hover:text-white transition-colors disabled:opacity-50"
            >
              {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
              Join
            </button>
          </div>
        </div>

        <div className="mt-12">
          <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">/ Your teams</p>
          <h2 className="mt-2 font-heading font-black text-3xl tracking-tight">{teams.length} space{teams.length === 1 ? "" : "s"}</h2>

          <div className="mt-6">
            {loading ? (
              <div data-testid="teams-loading" className="font-mono text-sm text-zinc-500">Loading…</div>
            ) : teams.length === 0 ? (
              <div data-testid="teams-empty" className="border-2 border-dashed border-[#BFDBFE] bg-white p-12 text-center">
                <Users className="w-8 h-8 mx-auto text-[#3B82F6]" />
                <p className="mt-3 font-heading font-bold text-xl">No teams yet</p>
                <p className="mt-1 text-zinc-500 text-sm">Create one above, or join with an invite code.</p>
              </div>
            ) : (
              <div data-testid="teams-list" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {teams.map((t) => (
                  <TeamCard key={t.id} team={t} onCopy={copyInvite} onLeave={leaveOrDelete} />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function TeamCard({ team, onCopy, onLeave }) {
  const [notes, setNotes] = useState(null);
  const [open, setOpen] = useState(false);

  const loadNotes = async () => {
    if (notes !== null) return setOpen((v) => !v);
    setOpen(true);
    try {
      const { data } = await api.get(`/teams/${team.id}/notes`);
      setNotes(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setNotes([]);
    }
  };

  return (
    <div data-testid={`team-card-${team.id}`} className="bg-white border-2 border-black p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-heading font-bold text-xl leading-tight">{team.name}</h3>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            {team.member_count} member{team.member_count === 1 ? "" : "s"} · {team.is_owner ? "Owner" : "Member"}
          </p>
        </div>
        <button
          data-testid={`leave-team-${team.id}`}
          onClick={() => onLeave(team)}
          className="p-2 hover:bg-[#FF3333] hover:text-white transition-colors"
          title={team.is_owner ? "Delete team" : "Leave team"}
        >
          {team.is_owner ? <Trash2 className="w-4 h-4" /> : <LogOut className="w-4 h-4" />}
        </button>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <div className="flex-1 px-3 py-2 bg-[#DBEAFE] font-mono text-xs">{team.invite_code}</div>
        <button
          data-testid={`copy-invite-${team.id}`}
          onClick={() => onCopy(team.invite_code)}
          className="p-2 border-2 border-black hover:bg-black hover:text-white transition-colors"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>

      <button
        data-testid={`toggle-team-notes-${team.id}`}
        onClick={loadNotes}
        className="mt-4 w-full font-mono text-xs uppercase tracking-widest px-3 py-2 border-2 border-black hover:bg-black hover:text-white transition-colors"
      >
        {open ? "Hide" : "Show"} shared notes
      </button>

      {open && (
        <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
          {notes === null ? (
            <p className="font-mono text-xs text-zinc-500">Loading…</p>
          ) : notes.length === 0 ? (
            <p className="font-mono text-xs text-zinc-500">No notes shared yet.</p>
          ) : (
            notes.map((n) => (
              <a
                key={n.id}
                href={`/app/note/${n.id}`}
                className="block px-3 py-2 bg-[#F0F9FF] hover:bg-[#DBEAFE] transition-colors text-sm"
              >
                <div className="font-bold truncate">{n.title}</div>
                <div className="text-xs text-zinc-500 truncate">{n.notes?.one_line_summary || ""}</div>
              </a>
            ))
          )}
        </div>
      )}
    </div>
  );
}
